import type { Database } from "@pex/database/types";
import type { SupabaseClient } from "@supabase/supabase-js";

function sharedSchema(client: SupabaseClient<Database>) {
  // biome-ignore lint/suspicious/noExplicitAny: schema() is not typed in the generated Database type
  return (client as unknown as any).schema("shared");
}

function retries(client: SupabaseClient<Database>) {
  return sharedSchema(client).from("subscription_retries");
}

const FIRST_RETRY_INTERVAL_MS = 3 * 24 * 60 * 60 * 1000;

// Postgres unique_violation — raised by subscription_retries_invoice_code_key
// when a retry row already exists for the invoice.
const UNIQUE_VIOLATION = "23505";

/**
 * Whether a dunning row already exists for this invoice, resolved or not.
 *
 * A failed renewal can reach us as invoice.create, invoice.update and
 * invoice.payment_failed, and all three share one payment_events id (the
 * invoice code), so the event log cannot tell a second event about the same
 * failure from the first. The retry row can: there is at most one per invoice.
 */
export async function retryExistsForInvoice(
  client: SupabaseClient<Database>,
  invoiceCode: string
): Promise<boolean> {
  const { data, error } = await retries(client)
    .select("id")
    .eq("invoice_code", invoiceCode)
    .limit(1)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to look up retry entry: ${error.message}`);
  }

  return data !== null;
}

/**
 * Creates the dunning row for a failed invoice and returns the moment our own
 * retry worker will next attempt the charge.
 *
 * Returns `created: false` when a row for this invoice already exists: two
 * events for the same failure raced past retryExistsForInvoice and the unique
 * index on invoice_code rejected the second. The caller must then skip the
 * rest of the failure path (retry count, dunning email), which the winner owns.
 *
 * The returned date is the only trustworthy retry signal we have. Paystack's
 * `subscription.next_payment_date` on a payment_failed event is the *next
 * billing cycle* (a month out), not the dunning retry, so emails that quoted it
 * told customers to expect a retry roughly a month later than one actually
 * happens. Callers should render this value instead.
 *
 * `failureReason` is best-effort: Paystack omits `gateway_response` on every
 * production invoice.payment_failed event (0/37 carry one), so this is usually
 * null on the first failure and only populated once the retry worker attempts
 * the charge directly and gets a gateway response back.
 */
export async function createRetryEntry(
  client: SupabaseClient<Database>,
  subscriptionId: string,
  invoiceCode: string,
  amount: number,
  currency: string,
  failureReason?: string | null
): Promise<
  | { created: true; nextAttemptAt: string }
  | { created: false; nextAttemptAt: null }
> {
  const nextAttemptAt = new Date(
    Date.now() + FIRST_RETRY_INTERVAL_MS
  ).toISOString();

  const { error } = await retries(client).insert({
    subscription_id: subscriptionId,
    invoice_code: invoiceCode,
    amount,
    currency,
    resolved: false,
    max_tries: 2,
    next_attempt_at: nextAttemptAt,
    last_error: failureReason ?? null,
  });

  if (error?.code === UNIQUE_VIOLATION) {
    return { created: false, nextAttemptAt: null };
  }

  if (error) {
    throw new Error(`Failed to create retry entry: ${error.message}`);
  }

  return { created: true, nextAttemptAt };
}

export async function resolveRetry(
  client: SupabaseClient<Database>,
  subscriptionId: string,
  invoiceCode: string
): Promise<void> {
  const { error } = await retries(client)
    .update({
      resolved: true,
      updated_at: new Date().toISOString(),
    })
    .eq("subscription_id", subscriptionId)
    .eq("invoice_code", invoiceCode)
    .eq("resolved", false);

  if (error) {
    throw new Error(`Failed to resolve retry entry: ${error.message}`);
  }
}
